"use client";

import TextHeader from "@/components/typography/TextHeader";
import Image from "next/image";
import getGalleryData from "@/hooks/data/getGalleryData";
import { clsx } from "clsx";
import { useState } from "react";
import { Gallery, Item } from "react-photoswipe-gallery";
import SwiperCarousel from "@/components/plugins/swiper/SwiperCarousel";
import { SwiperSlide } from "swiper/react";
import { useMediaQuery } from "@/hooks/ui/useMediaQuery";
import Button from "@/components/ui/Button";
import IconChevronDown from "@/components/icons/IconChevronDown";
import IconDateTime from "@/components/icons/IconDateTime";

const formatSessionDate = (isoDate: string) =>
  new Intl.DateTimeFormat("pl-PL", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(isoDate));

export default function GallerySlider() {
  const gallery = getGalleryData().filter(
    (group) => group.images.length > 0 || group.sessions?.length,
  );
  const [selectedGroupId, setSelectedGroupId] = useState("visualizations");
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null,
  );
  const selectedGroup =
    gallery.find((group) => group.id === selectedGroupId) ?? gallery[0];
  const selectedSession =
    selectedGroup.sessions?.find(
      (session) => session.id === selectedSessionId,
    ) ?? selectedGroup.sessions?.[0];
  const items = selectedSession?.images ?? selectedGroup.images;
  const gridMobile = [
    "aspect-square col-span-2",
    "aspect-square col-span-1",
    "aspect-square col-span-1",
    "aspect-square col-span-1",
    "aspect-square col-span-1",
  ];
  const gridDesktop = [
    "lg:aspect-auto lg:col-span-2",
    "lg:aspect-square lg:col-span-1",
    "lg:aspect-square lg:col-span-1",
    "lg:aspect-square lg:col-span-1",
    "lg:aspect-square lg:col-span-1",
    "lg:aspect-auto lg:col-span-2",
    "lg:aspect-square lg:col-span-1",
    "lg:aspect-square lg:col-span-1",
  ];
  const itemsPerSlide = [3, 2, 3];
  const slideWidths = [594, 287, 594]; // px widths for each slide in pattern

  // Generate slides for desktop gallery based on itemsPerSlide pattern
  const generateSlides = () => {
    const slides = [];
    let itemIndex = 0;
    const patternLength = itemsPerSlide.length;
    let patternIndex = 0;

    while (itemIndex < items.length) {
      const itemsInCurrentSlide = itemsPerSlide[patternIndex % patternLength];
      const slideWidth = slideWidths[patternIndex % patternLength];
      const slideItems = items.slice(
        itemIndex,
        itemIndex + itemsInCurrentSlide,
      );

      slides.push(
        <SwiperSlide key={itemIndex} style={{ width: `${slideWidth}px` }}>
          <div className="relative grid grid-flow-col grid-rows-2 gap-5">
            {slideItems.map((image, idx) => {
              const absoluteIndex = itemIndex + idx;
              const aspectRatio = image.original.height / image.original.width;
              return (
                <Gallery key={absoluteIndex}>
                  <Item
                    original={image.original.src}
                    thumbnail={image.thumbnail.src}
                    width={image.original.width}
                    height={image.original.height}
                  >
                    {({ ref, open }) => (
                      <div
                        ref={ref}
                        onClick={open}
                        className={clsx(
                          gridDesktop[absoluteIndex % 8],
                          "cursor-pointer overflow-hidden rounded-lg",
                        )}
                      >
                        <div className="relative h-full w-full">
                          <Image
                            src={image.thumbnail}
                            alt=""
                            width={600}
                            height={600 * aspectRatio}
                            className="absolute inset-0 h-full w-full object-cover"
                          />
                        </div>
                      </div>
                    )}
                  </Item>
                </Gallery>
              );
            })}
          </div>
        </SwiperSlide>,
      );

      itemIndex += itemsInCurrentSlide;
      patternIndex++;
    }

    return slides;
  };

  const isMobile = useMediaQuery("(max-width: 768px)");
  let galleryContent = null;
  if (1) {
    galleryContent = (
      <div
        key={`${selectedGroup.id}-${selectedSession?.id ?? ""}`}
        className="relative mx-5 grid grid-cols-2 gap-5 lg:grid-flow-col lg:grid-cols-none lg:grid-rows-2 [&>*:nth-child(n+9)]:hidden lg:[&>*:nth-child(n+9)]:block"
      >
        <Gallery>
          {items.map((image, index) => {
            const aspectRatio = image.original.height / image.original.width;
            return (
              <Item
                key={index}
                original={image.original.src}
                thumbnail={image.thumbnail.src}
                width={image.original.width}
                height={image.original.height}
              >
                {({ ref, open }) => (
                  <div
                    ref={ref}
                    onClick={open}
                    key={index}
                    className={clsx(
                      gridMobile[index % 5],
                      gridDesktop[index % 8],
                      "overflow-hidden rounded-lg",
                    )}
                  >
                    <div className="relative h-full w-full">
                      <Image
                        src={image.thumbnail}
                        alt={""}
                        width={600}
                        height={600 * aspectRatio}
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    </div>
                  </div>
                )}
              </Item>
            );
          })}
        </Gallery>
      </div>
    );
  } else {
    // galleryContent = (
    //   <SwiperCarousel
    //     freemode={true}
    //     className={"!px-0"}
    //     slidesPerView={"auto"}
    //     breakpoints={{}}
    //   >
    //     {generateSlides()}
    //   </SwiperCarousel>
    // );
  }

  return (
    <div className="mb-16 lg:mb-24" id={"gallery-images"}>
      <div className="m-auto mb-7 max-w-screen-sm px-5 lg:mb-12">
        <TextHeader className="mb-9">Galeria</TextHeader>
        {gallery.length > 1 && (
          <div className="flex justify-between gap-3 sm:gap-5">
            {gallery.map((group) => (
              <Button
                theme={selectedGroup.id === group.id ? "black" : "gray"}
                inverse={selectedGroup.id !== group.id}
                key={group.id}
                className="!w-auto flex-grow !px-3 sm:!px-5"
                onClick={() => setSelectedGroupId(group.id)}
              >
                {group.icon && group.icon}
                <span>{group.name}</span>
              </Button>
            ))}
          </div>
        )}
        {selectedGroup.sessions && selectedSession && (
          <div className="relative mt-5">
            <label className="sr-only" htmlFor="gallery-session">
              Data sesji zdjęciowej
            </label>
            <IconDateTime className="pointer-events-none absolute left-4 top-1/2 w-7 -translate-y-1/2" />
            <select
              id="gallery-session"
              value={selectedSession.id}
              onChange={(e) => setSelectedSessionId(e.target.value)}
              className="h-14 w-full cursor-pointer appearance-none rounded-lg border-2 border-[#DDDDE9] bg-white pl-14 pr-12 text-[16px]/[24px] font-semibold lg:text-[18px]/[24px]"
            >
              {selectedGroup.sessions.map((session) => (
                <option key={session.id} value={session.id}>
                  {formatSessionDate(session.date)}
                </option>
              ))}
            </select>
            <IconChevronDown className="pointer-events-none absolute right-3 top-1/2 w-8 -translate-y-1/2" />
          </div>
        )}
      </div>
      {galleryContent}
    </div>
  );
}
